"use client";

import * as React from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/03-presenters/components/ui/tooltip";
import { compareDesc, endOfMonth, format, getDay, getHours, parse, startOfMonth, startOfWeek, subDays, subMonths } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/03-presenters/components/ui/popover";
import { Button } from "@/03-presenters/components/ui/button";
import { Calendar } from "@/03-presenters/components/ui/calendar";
import MonthPicker from "@/03-presenters/components/ui/month-picker";
import WeekPicker, { getLastSaturday } from "@/03-presenters/components/ui/week-picker";
import { Info } from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "@/03-presenters/components/ui/toggle-group";
import { useLocale, useTranslations } from "next-intl";
import { parseAsInteger, parseAsString, useQueryState, parseAsJson } from "nuqs";
import { BEIJING_NOW_DATE, CountryCode, DEFAULT_START_DATE, DEFAULT_START_DATE_FUNC, europeList } from "@/00-shared/const";
import { CustomizedTimeFilter } from "@/01-core/entity/entities/entity";
import { locales } from "@/i18n";
import { useDisabledDate } from "@/03-presenters/shared/hooks";
import { countryStore } from "@/03-presenters/components/MultiCountrySelect";
import { useUpdateEffect } from "ahooks";
export enum DateType {
  Day = "0",
  Week = "1",
  Month = "2",
}
const now = BEIJING_NOW_DATE;
const patterns = "yyyy-MM-dd";
const urlDateFormat = (date: Date) => format(date, "yyyyMMdd");
const stringToDate = (dateString: string): Date => {
  const year = parseInt(dateString.substring(0, 4), 10);
  const month = parseInt(dateString.substring(4, 6), 10) - 1;
  const day = parseInt(dateString.substring(6, 8), 10);
  return new Date(year, month, day);
};
const getFirstMonday = (date: Date) => startOfWeek(date, {
  weekStartsOn: 1
});
const defaultSelectWeekDate = (() => {
  // 周一下午前三点，返回前两周
  if (getDay(now) === 1 && getHours(now) < 15) {
    return getFirstMonday(subDays(now, 14));
  }
  return getFirstMonday(subDays(now, 7));
})();
const defaultSelectMonthDate = (() => {
  // 1号下午前三点，返回前两月
  if (format(now, "dd") === "01" && getHours(now) < 15) {
    return startOfMonth(subMonths(now, 2));
  }
  return startOfMonth(subMonths(now, 1));
})();
const dateFormat = ({
  startDateTime,
  dateType,
  to
}: {
  startDateTime: string;
  dateType: DateType;
  to: string;
}) => {
  const today = new Date();
  const parseDate = parse(startDateTime, "yyyyMMdd", new Date());
  switch (dateType) {
    case DateType.Month:
      const monthFrom = format(startOfMonth(parseDate), patterns);
      const maxMonthTo = compareDesc(endOfMonth(parseDate), today) === 1 ? endOfMonth(parseDate) : today;
      const monthTo = format(maxMonthTo, patterns);
      return `${monthFrom} ${to} ${monthTo}`;
    case DateType.Week:
      const weekFrom = format(getFirstMonday(parseDate), patterns);
      const maxWeekTo = compareDesc(getLastSaturday(parseDate), today) === 1 ? getLastSaturday(parseDate) : today;
      const weekTo = format(maxWeekTo, patterns);
      return `${weekFrom} ${to} ${weekTo}`;
    default:
      return format(parseDate, patterns);
  }
};
export function DatePickerType() {
  const t = useTranslations("EntityView.filter");
  const countryCode = countryStore(state => state.countryCode);
  const shouldShowWeekPicker = !europeList.includes(countryCode as CountryCode);
  const shouldShowMonthPicker = !europeList.includes(countryCode as CountryCode);
  const [startDateTime, setStartDateTime] = useQueryState("start_date_time", parseAsString.withDefault(urlDateFormat(DEFAULT_START_DATE)));
  const [datePickerType, setGranularity] = useQueryState("granularity", parseAsInteger.withDefault(0));
  useUpdateEffect(() => {
    setGranularity(0);
    setStartDateTime(urlDateFormat(DEFAULT_START_DATE));
  }, [countryCode, setGranularity, setStartDateTime]);
  const [_, setPageIndex] = useQueryState("pageIndex", parseAsInteger);
  const onValueChange = (val: DateType) => {
    if (!val) {
      return;
    }
    if (val === DateType.Day) {
      setStartDateTime(urlDateFormat(DEFAULT_START_DATE));
    }
    if (val === DateType.Week) {
      setStartDateTime(urlDateFormat(defaultSelectWeekDate));
    }
    if (val === DateType.Month) {
      setStartDateTime(urlDateFormat(defaultSelectMonthDate));
    }
    setGranularity(Number(val));
    setPageIndex(0);
  };
  return <ToggleGroup value={datePickerType.toString()} onValueChange={onValueChange} type="single" data-sentry-element="ToggleGroup" data-sentry-component="DatePickerType" data-sentry-source-file="DatePicker.tsx">
      <ToggleGroupItem className="data-[state=on]:gradient-btn hover:gradient-text data-[state=on]:hover:gradient-btn my-1  h-7 text-nowrap font-normal text-default-black duration-0  data-[state=on]:text-white hover:bg-transparent  data-[state=on]:hover:text-white" value={DateType.Day} aria-label="Toggle Daily" data-sentry-element="ToggleGroupItem" data-sentry-source-file="DatePicker.tsx">
        {t("day")}
      </ToggleGroupItem>
      {shouldShowWeekPicker && <ToggleGroupItem className="data-[state=on]:gradient-btn hover:gradient-text data-[state=on]:hover:gradient-btn my-1  h-7 text-nowrap font-normal text-default-black duration-0  data-[state=on]:text-white hover:bg-transparent  data-[state=on]:hover:text-white" value={DateType.Week} aria-label="Toggle Weekly">
          {t("week")}
        </ToggleGroupItem>}

      {shouldShowMonthPicker && <ToggleGroupItem className="data-[state=on]:gradient-btn hover:gradient-text data-[state=on]:hover:gradient-btn my-1  h-7 text-nowrap font-normal text-default-black duration-0  data-[state=on]:text-white hover:bg-transparent  data-[state=on]:hover:text-white" value={DateType.Month} aria-label="Toggle Monthly">
          {t("month")}
        </ToggleGroupItem>}
    </ToggleGroup>;
}
export function DatePicker({
  isCustomized,
  fieldKey,
  isDashboard
}: {
  isCustomized?: boolean;
  fieldKey?: string;
  isDashboard?: boolean;
}) {
  const disabledDate = useDisabledDate(isDashboard);
  const [startDateTime, setStartDateTime] = useQueryState("start_date_time", parseAsString.withDefault(urlDateFormat(DEFAULT_START_DATE)));
  const t = useTranslations("EntityView.filter");
  const locale = useLocale() as locales;
  const [_, setPageIndex] = useQueryState("pageIndex", parseAsInteger);
  const [dateType] = useQueryState("granularity", parseAsString.withDefault("0"));
  const [customizedTimeFilter, setCustomizedTimeFilter] = useQueryState("customizedTimeFilter", parseAsJson<CustomizedTimeFilter>().withDefault([{
    field_key: fieldKey ?? "",
    start_date_time: urlDateFormat(subDays(new Date(), 3))
  }]));
  React.useEffect(() => {
    if (isCustomized) {
      setCustomizedTimeFilter([{
        field_key: fieldKey!,
        start_date_time: urlDateFormat(subDays(new Date(), 3))
      }]);
    }
  }, []);
  const dayPickerTime = isCustomized ? customizedTimeFilter[0].start_date_time : startDateTime;
  const onSelect = (date?: Date) => {
    if (date && isCustomized && fieldKey) {
      setCustomizedTimeFilter([{
        field_key: fieldKey,
        start_date_time: urlDateFormat(date)
      }]);
      return;
    }
    if (date) {
      setStartDateTime(urlDateFormat(date));
      setPageIndex(0);
    }
  };
  const pickerMap = {
    [DateType.Day]: <Calendar mode="single" selected={stringToDate(dayPickerTime!)} onSelect={onSelect} defaultMonth={stringToDate(dayPickerTime!)} initialFocus disabled={{
      before: isCustomized ? undefined : disabledDate,
      after: isCustomized ? subDays(new Date(), 3) : DEFAULT_START_DATE_FUNC()
    }} />,
    [DateType.Week]: <WeekPicker selected={stringToDate(startDateTime)} onSelect={onSelect} defaultMonth={stringToDate(startDateTime)} isDashboard={isDashboard} />,
    [DateType.Month]: <MonthPicker currentMonth={stringToDate(startDateTime)} onMonthChange={onSelect} isDashboard={isDashboard} />
  };
  const currentCalendar = pickerMap[dateType as DateType];
  const DisplayDate = () => {
    if (isCustomized) {
      return <span>{format(stringToDate(dayPickerTime!), patterns)}</span>;
    }
    return startDateTime ? <span>
        {dateFormat({
        startDateTime,
        dateType: dateType as DateType,
        to: t("to")
      })}
      </span> : <span>{t("dayPickerPlaceholder")}</span>;
  };
  return <div className="flex items-center gap-2" data-sentry-component="DatePicker" data-sentry-source-file="DatePicker.tsx">
      <Popover data-sentry-element="Popover" data-sentry-source-file="DatePicker.tsx">
        <PopoverTrigger asChild data-sentry-element="PopoverTrigger" data-sentry-source-file="DatePicker.tsx">
          <Button variant={"outline"} className={cn("min-w-[280px] justify-start text-left font-normal", !startDateTime && "text-muted-foreground")} data-sentry-element="Button" data-sentry-source-file="DatePicker.tsx">
            <CalendarIcon className="mr-2 h-4 w-4" data-sentry-element="CalendarIcon" data-sentry-source-file="DatePicker.tsx" />
            <DisplayDate data-sentry-element="DisplayDate" data-sentry-source-file="DatePicker.tsx" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" data-sentry-element="PopoverContent" data-sentry-source-file="DatePicker.tsx">
          {currentCalendar}
        </PopoverContent>
      </Popover>
    </div>;
}
export { defaultSelectWeekDate as DEFAULT_SELECT_WEEK_DATE, defaultSelectMonthDate as DEFAULT_SELECT_MONTH_DATE };